import { useState, useEffect, useCallback } from 'react'
import { serverApi } from '../api'
import {
  HiCheckCircle, HiXCircle, HiArrowPath, HiArrowUturnLeft,
  HiSparkles, HiChevronDown, HiChevronRight, HiClock
} from 'react-icons/hi2'
import { Loader } from '../ui'

const STATUS = {
  success:     { label: 'Muvaffaqiyatli', color: 'var(--ui-st-ok)',       bg: 'var(--ui-st-ok-bg)',       Icon: HiCheckCircle },
  failed:      { label: 'Yiqildi',        color: 'var(--ui-st-critical)', bg: 'var(--ui-st-critical-bg)', Icon: HiXCircle },
  rolled_back: { label: 'Orqaga qaytdi',  color: 'var(--ui-st-high)',     bg: 'var(--ui-st-high-bg)',     Icon: HiArrowUturnLeft },
  running:     { label: 'Bajarilmoqda',   color: 'var(--ui-st-neutral)',  bg: 'var(--ui-st-neutral-bg)',  Icon: HiArrowPath },
}

function fmtDate(iso) {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString('uz-UZ') } catch { return iso }
}
function fmtDur(sec) {
  if (!sec && sec !== 0) return ''
  if (sec < 60) return `${sec}s`
  return `${Math.floor(sec / 60)}m ${sec % 60}s`
}

/** AI tahlilini bo'limlarga ajratib chiroyli ko'rsatadi */
function AnalysisView({ text }) {
  if (!text) return null
  const sections = []
  const re = /(MUAMMO|SABAB|YECHIM|OLDINI OLISH)\s*:/g
  let m, last = null, lastIdx = 0
  while ((m = re.exec(text)) !== null) {
    if (last) sections.push([last, text.slice(lastIdx, m.index).trim()])
    last = m[1]; lastIdx = m.index + m[0].length
  }
  if (last) sections.push([last, text.slice(lastIdx).trim()])

  if (sections.length === 0) {
    return <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, margin: 0, lineHeight: 1.6 }}>{text}</pre>
  }

  const colors = {
    'MUAMMO': 'var(--ui-st-critical)', 'SABAB': 'var(--ui-st-high)',
    'YECHIM': 'var(--ui-st-ok)', 'OLDINI OLISH': 'var(--ui-text-1)',
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {sections.map(([title, body]) => (
        <div key={title}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: colors[title] || 'var(--ui-text-2)', marginBottom: 4 }}>
            {title}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{body}</div>
        </div>
      ))}
    </div>
  )
}

function DeploymentRow({ dep, onRefresh }) {
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [analysis, setAnalysis] = useState(dep.ai_analysis || null)
  const [err, setErr] = useState(null)

  const st = STATUS[dep.status] || STATUS.running
  const { Icon } = st

  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (next && !detail) {
      setLoading(true)
      try { setDetail(await serverApi.getDeployment(dep.id)) }
      catch (e) { setErr(e.message) }
      finally { setLoading(false) }
    }
  }

  const runAnalysis = async () => {
    setAnalyzing(true); setErr(null)
    try {
      const res = await serverApi.analyzeDeployment(dep.id)
      setAnalysis(res.analysis)
      onRefresh?.()
    } catch (e) { setErr(e.message) }
    finally { setAnalyzing(false) }
  }

  return (
    <div style={{ border: '1px solid var(--ui-border)', borderRadius: 8, overflow: 'hidden' }}>
      <div onClick={toggle} style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        cursor: 'pointer', background: open ? 'var(--ui-surface-2)' : 'transparent'
      }}>
        {open ? <HiChevronDown size={16} /> : <HiChevronRight size={16} />}
        <Icon size={18} style={{ color: st.color, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {(dep.commit_message || '(izohsiz)').split('\n')[0]}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ui-text-2)', display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 2 }}>
            {dep.commit_short && <span style={{ fontFamily: 'var(--ui-mono)' }}>{dep.commit_short}</span>}
            {dep.commit_author && <span>{dep.commit_author}</span>}
            <span><HiClock size={10} style={{ verticalAlign: -1 }} /> {fmtDate(dep.started_at)}</span>
            {dep.duration_sec != null && <span>{fmtDur(dep.duration_sec)}</span>}
          </div>
        </div>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 6,
          color: st.color, background: st.bg, whiteSpace: 'nowrap'
        }}>{st.label}</span>
      </div>

      {open && (
        <div style={{ padding: 16, borderTop: '1px solid var(--ui-border)', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {err && <div style={{ color: 'var(--ui-st-critical)', fontSize: 12 }}>{err}</div>}

          {dep.error_summary && (
            <div style={{ background: 'var(--ui-st-critical-bg)', border: '1px solid var(--ui-st-critical)', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: 'var(--ui-st-critical)' }}>
              {dep.error_summary}
            </div>
          )}

          {(analysis || dep.status !== 'success') && (
            <div style={{ background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: analysis ? 12 : 0 }}>
                <HiSparkles size={16} style={{ color: 'var(--ui-text-1)' }} aria-hidden="true" />
                <b style={{ fontSize: 13, flex: 1 }}>AI tahlili</b>
                {!analysis && (
                  <button className="btn btn-sm" onClick={runAnalysis} disabled={analyzing}>
                    {analyzing ? 'Tahlil qilinmoqda...' : 'Nima uchun yiqildi?'}
                  </button>
                )}
                {analysis && (
                  <button className="btn btn-sm" onClick={runAnalysis} disabled={analyzing}>
                    {analyzing ? '...' : 'Qayta tahlil'}
                  </button>
                )}
              </div>
              {analysis && <AnalysisView text={analysis} />}
            </div>
          )}

          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Loglar</div>
            {loading ? <Loader state="working" size="sm" /> : (
              <pre style={{
                background: 'var(--ui-surface-2)', color: 'var(--ui-text-1)',
                border: '1px solid var(--ui-border)', padding: 12, borderRadius: 6,
                fontFamily: 'var(--ui-mono)', fontSize: 11, lineHeight: 1.5,
                maxHeight: 320, overflow: 'auto',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0
              }}>{detail?.logs || '(loglar yo\'q)'}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function DeploymentHistory() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    setLoading(true); setErr(null)
    try {
      const res = await serverApi.listDeployments(30)
      setItems(res.deployments || [])
    } catch (e) { setErr(e.message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const failed = items.filter(d => d.status !== 'success').length

  return (
    <div className="card">
      <div className="card-hdr" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0 }}>CI/CD Tarixi</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ui-text-2)' }}>
            Har bir deploy: holati, loglari va AI tahlili
            {failed > 0 && <> · <span style={{ color: 'var(--ui-st-critical)' }}>{failed} ta muammoli</span></>}
          </p>
        </div>
        <button className="btn btn-sm" onClick={load} disabled={loading}>
          <HiArrowPath size={14} /> Yangilash
        </button>
      </div>
      <div className="card-body">
        {err && <div style={{ color: 'var(--ui-st-critical)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
        {loading && items.length === 0 ? (
          <Loader state="searching" />
        ) : items.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--ui-text-2)', lineHeight: 1.6 }}>
            Hali deploy yozuvlari yo'q.<br />
            GitHub'ga push qilinganda bu yerda avtomatik paydo bo'ladi
            (GitHub Secrets: <code>DEPLOY_REPORT_URL</code> va <code>DEPLOY_WEBHOOK_SECRET</code> sozlangan bo'lishi kerak).
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(d => <DeploymentRow key={d.id} dep={d} onRefresh={load} />)}
          </div>
        )}
      </div>
    </div>
  )
}
